/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { formatDate } from 'sharedUi/dateUtils';
import type { AccessibleProject } from '../../services/projects.service';
import type { Agent } from '../../types';
import type { Conversation } from '../../types/conversation.types';

interface Props {
  projects: AccessibleProject[];
  selectedProjectId: string | null;
  onSelectProject: (id: string | null) => void;
  agents: Agent[];
  selectedAgentCode: string | null;
  onSelectAgent: (code: string) => void;
  loadingAgents?: boolean;
  conversations: Conversation[];
  activeConversationId: string | null;
  onSelectConversation: (id: string) => void;
  loadingConversations?: boolean;
  onNewChat: () => void;
  canCreate?: boolean;
}

const sectionLabel = 'px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-muted';

/** The chat workspace rail: Project → Co-pilots → Chats. */
const ConversationSidebar = (p: Props) => {
  const projectOptions = p.projects.map((pr) => ({ label: pr.appCode ? `${pr.name} (${pr.appCode})` : pr.name, value: pr._id }));

  return (
    <aside className="flex h-full w-72 flex-col border-r border-line bg-surface">
      {/* Project switcher — only when there's a choice. */}
      {p.projects.length > 1 && (
        <div className="border-b border-line p-3">
          <Dropdown value={p.selectedProjectId} options={projectOptions} filter
            onChange={(e: any) => p.onSelectProject(e.value || null)}
            placeholder="Project" className="w-full" />
        </div>
      )}

      {/* Co-pilots (conversational agents in this project). */}
      <div className="shrink-0">
        <div className={sectionLabel}>Co-pilots</div>
        <div className="max-h-48 overflow-y-auto px-2 pb-2">
          {p.loadingAgents ? (
            <p className="px-2 py-1 text-xs text-ink-muted">Loading…</p>
          ) : p.agents.length === 0 ? (
            <p className="px-2 py-1 text-xs text-ink-muted">No chat-enabled agents in this project.</p>
          ) : (
            p.agents.map((a) => {
              const active = a.agentCode === p.selectedAgentCode;
              return (
                <button key={a.agentCode} type="button" onClick={() => onSelectAgentSafe(p, a.agentCode)}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${active ? 'bg-primary/10 font-medium text-primary' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'}`}>
                  <i className="pi pi-android text-xs" aria-hidden />
                  <span className="truncate">{a.name || a.agentCode}</span>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Chats for the selected co-pilot. */}
      <div className="flex items-center justify-between border-t border-line">
        <div className={sectionLabel}>Chats</div>
        {p.canCreate && p.selectedAgentCode && (
          <Button icon="pi pi-plus" rounded text size="small" aria-label="New chat" title="New chat"
            onClick={p.onNewChat} className="mr-2" />
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {p.loadingConversations ? (
          <p className="px-2 py-1 text-xs text-ink-muted">Loading…</p>
        ) : !p.selectedAgentCode ? (
          <p className="px-2 py-1 text-xs text-ink-muted">Pick a co-pilot above.</p>
        ) : p.conversations.length === 0 ? (
          <p className="px-2 py-1 text-xs text-ink-muted">No chats yet — start one.</p>
        ) : (
          p.conversations.map((c) => {
            const active = c._id === p.activeConversationId;
            return (
              <button key={c._id} type="button" onClick={() => p.onSelectConversation(c._id)}
                className={`flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left ${active ? 'bg-primary/10' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                <span className={`w-full truncate text-sm ${active ? 'font-medium text-primary' : 'text-gray-800 dark:text-gray-200'}`}>
                  {c.title || 'Untitled conversation'}
                </span>
                <span className="text-[10px] text-ink-muted">{formatDate(c.lastMessageAt || c.updatedAt || c.createdAt)}</span>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
};

function onSelectAgentSafe(p: Props, code: string) {
  if (code !== p.selectedAgentCode) p.onSelectAgent(code);
}

export default ConversationSidebar;
